import { DomainCriteria } from 'core_main/api/settings/types';
import { getEnv } from 'core_main/globals/env';
import { getStore } from 'core_main/store';
import { IsUrlAllowedParameters, IsUrlAllowedResponse } from './types';

const RESERVED_PLATFORM_SUBDOMAINS = new Set([
  'api',
  'cdn',
  'www',
  'static',
  'assets',
]);
const HELPAPP_DOMAIN_FRAGMENT = 'helpapp';
const HELPAPP_WEB_SUFFIX = `.${HELPAPP_DOMAIN_FRAGMENT}.io`;
const KUSTOMERAPP_DOMAIN_FRAGMENT = 'kustomerapp';
const KUSTOMERAPP_WEB_SUFFIX = `.${KUSTOMERAPP_DOMAIN_FRAGMENT}.com`;

// Mirrors api-gw-chat-go platformDomainSuffixes; scoped per widget via baseUrlDomain.
export const getPlatformWebSuffixFromBaseUrlDomain = (
  baseUrlDomain?: string,
): string | null => {
  if (!baseUrlDomain) {
    return null;
  }
  const lower = baseUrlDomain.toLowerCase();
  if (lower.includes(HELPAPP_DOMAIN_FRAGMENT)) {
    return HELPAPP_WEB_SUFFIX;
  }
  if (lower.includes(KUSTOMERAPP_DOMAIN_FRAGMENT)) {
    return KUSTOMERAPP_WEB_SUFFIX;
  }
  return null;
};

export const isSameTenantPlatformUrl = (
  url: string,
  orgName?: string,
  platformWebSuffix?: string | null,
): boolean => {
  if (!url || typeof orgName !== 'string' || !orgName || !platformWebSuffix) {
    return false;
  }

  let hostname: string;
  try {
    hostname = new URL(url).hostname.toLowerCase();
  } catch {
    return false;
  }

  const hostWithoutPort = hostname.split(':')[0];
  if (!hostWithoutPort.endsWith(platformWebSuffix)) {
    return false;
  }

  const subdomain = hostWithoutPort.slice(0, -platformWebSuffix.length);
  if (!subdomain) {
    return false;
  }

  const labels = subdomain.split('.');
  if (labels.length !== 1) {
    return false;
  }

  const tenantLabel = labels[0];
  if (!tenantLabel) {
    return false;
  }

  if (RESERVED_PLATFORM_SUBDOMAINS.has(tenantLabel)) {
    return false;
  }

  return tenantLabel.toLowerCase() === orgName.toLowerCase();
};

export const validateIsUrlAllowedParameters = (
  parameters: IsUrlAllowedParameters,
) => {
  const url = parameters?.url;

  if (typeof url !== 'string') {
    throw Error('A url must be provided.');
  }

  return undefined;
};

export const createIsUrlAllowedResponse = (
  allowed: boolean,
): IsUrlAllowedResponse => {
  return {
    allowed,
  };
};
export const determineConditions = (
  criteria: DomainCriteria[],
  location: string,
  conditional: 'and' | 'or',
) => {
  let denied = true;
  const locationNoTrailingSlash = location.replace(/\/$/, '');

  if (!criteria) return false;
  // eslint-disable-next-line no-restricted-syntax
  for (const condition of criteria) {
    const { operator, value } = condition.website_url;
    const equalsOperator = operator === 'equals';
    const notEqualsOperator = operator === 'not_equals';
    const containsOperator = operator === 'contains';
    const notContainsOperator = operator === 'not_contains';

    // Admins may forget to add a trailing slash to a URL in the admin,
    //  we do not want to exclude those locations, so compare the location and
    //  the location without a trailing slash (if the slash exists)
    const locationIsEqual =
      location === value || locationNoTrailingSlash === value;

    if (
      (containsOperator && location.includes(value)) ||
      (notContainsOperator && !location.includes(value)) ||
      (equalsOperator && locationIsEqual) ||
      (notEqualsOperator && location !== value)
    ) {
      denied = false;
    } else if (conditional === 'and') {
      denied = true;
      break;
    }
  }

  return denied;
};

export const isUrlDenied = (currentLocation: string) => {
  const { orgName, baseUrlDomain } = getEnv();
  const platformWebSuffix =
    getPlatformWebSuffixFromBaseUrlDomain(baseUrlDomain);
  if (isSameTenantPlatformUrl(currentLocation, orgName, platformWebSuffix)) {
    return false;
  }

  const store = getStore();
  const { domainCriteria } = store.settings;

  if (!domainCriteria) return false;

  const orConditions = domainCriteria.or;
  const andConditions = domainCriteria.and;

  const deniedInAndConditions = determineConditions(
    andConditions,
    currentLocation,
    'and',
  );

  const deniedInOrConditions = determineConditions(
    orConditions,
    currentLocation,
    'or',
  );

  let isDenied = false;

  if (andConditions && orConditions) {
    const andPasses = !deniedInAndConditions && !deniedInOrConditions;
    isDenied = !andPasses;
  } else if (orConditions) {
    isDenied = deniedInOrConditions;
  } else if (andConditions) {
    isDenied = deniedInAndConditions;
  }

  return isDenied;
};
